<script lang="ts" setup>
import type { PropType} from 'vue';
import { onMounted, onUnmounted, ref } from 'vue';
import type { ICellRendererParams } from 'ag-grid-community';
import type { BadgeVariant } from '@/components/common/Badge.vue';
import Badge from '@/components/common/Badge.vue';

type ExtendedParams = ICellRendererParams & {
    id: string;
    title: (params: ICellRendererParams) => string | null;
    tooltip: (params: ICellRendererParams) => string | null;
    variant: (params: ICellRendererParams) => BadgeVariant;
    isLight: (params: ICellRendererParams) => boolean;
    readonly: (params: ICellRendererParams) => boolean;
    dataTest?: string;
};

const props = defineProps({
    params: {
        type: Object as PropType<ExtendedParams>,
        required: true
    }
});

const title = ref<string | null>(null);
const tooltip = ref<string | null>(null);
const variant = ref<BadgeVariant>('primary');
const readonly = ref<boolean>(false);
const isLight = ref<boolean>(false);
const dataTest = ref<string | undefined>(undefined);

const isEditing = ref(false);

onMounted(() => {
    props.params.api.addEventListener('cellEditingStarted', event => {
        if (event.node.id === props.params.node.id && event.colDef?.field === props.params.colDef?.field) {
            isEditing.value = event.node.id === props.params.node.id;
        }
    });
    props.params.api.addEventListener('cellEditingStopped', () => {
        isEditing.value = false;
    });
    title.value = typeof props.params.title === 'function' ? props.params.title(props.params) : props.params.title;
    tooltip.value = typeof props.params.tooltip === 'function' ? props.params.tooltip(props.params) : props.params.tooltip;
    readonly.value = typeof props.params.readonly === 'function' ? props.params.readonly(props.params) : props.params.readonly;
    variant.value = typeof props.params.variant === 'function' ? props.params.variant(props.params) : props.params.variant;
    isLight.value = typeof props.params.isLight === 'function' ? props.params.isLight(props.params) : props.params.isLight;
    dataTest.value = props.params.dataTest;
});

onUnmounted(() => {
    if (props.params.api.isDestroyed()) return;
    props.params.api.removeEventListener('cellEditingStarted', () => {});
    props.params.api.removeEventListener('cellEditingStopped', () => {});
});
</script>

<template>
    <div :class="['sy-edit-field', {'sy-default': readonly, 'editing': isEditing}]">
        <Badge
            v-if="title != null"
            v-tooltip="tooltip ?? title"
            :text="title"
            :data-test="dataTest"
            :small="true"
            :is-light="isLight"
            :variant="variant"
        />
        <div v-else></div>
        <div v-if="!readonly" class="flex-container align-middle">
            <Icon icon="edit" class="sy-edit-field-icon" />
        </div>
    </div>
</template>
<style lang="scss" scoped>
    .ag-cell {
        &:hover {
            .sy-edit-field-icon {
                display: block;
            }
        }
    }
    .sy-edit-field {
        display: flex;
        align-items: center;
        height: 100%;
        padding: 0 var(--sy-spacer-8);
        justify-content: space-between;

        .sy-edit-field-icon {
            display: none;
        }
        &.editing {
            background-color: var(--sy-blue-100);
            color: var(--sy-blue-600);
        }
    }
</style>
